/**
 * The §8 gate, as a function: turn a human's two answers about one hand into the
 * Document that will graft it.
 *
 * WHY THIS IS A LOAD-DOOR OPERATION AND NOT A COMMAND (ADR-007 §4). A graft
 * changes the ARMATURE — it adds twenty bones per hand that poses and keys can
 * then address. Undo is a pointer over retained Documents, so an undone graft
 * would strand every edit made against bones that no longer exist. `target` has
 * no command verbs for exactly this reason, and declaring a hand is a `target`
 * edit. So this composes a NEW Document and the caller re-runs it, beginning a
 * fresh lineage, the same way dropping a model does. Re-pinning a thumb is
 * therefore a re-declaration, not an undo.
 *
 * IT COMPOSES; IT DOES NOT VALIDATE. `parseDocument` begins a lineage, and
 * ADR-007 §4 gives that to the loader alone — machine-checked, and the check
 * caught this file's first draft calling it here. So these return a composed
 * Document for `app/src/main.ts` to take through the load door, which is where
 * the refusal belongs anyway: a malformed resolution or a forged `confirmedBy`
 * refuses at the same gate a malformed dropped file does, and still long before
 * anything is voxelized.
 *
 * THE CONFIRMATIONS ARE THE CALLER'S TO EARN. `confirmedBy` is schema-bound to
 * `^human:\S+$`, and the Document's own docs state the rule this file obeys: no
 * code path in this tree may mint one from an analyzer read. That is why both
 * answers are REQUIRED parameters with no defaults — there is no arrangement of
 * arguments that lets a caller graft a hand without having asked a person. The
 * remaining half of the rule lives at the call site, which must gate on a
 * trusted user event (XD brief §5.6); a function cannot check that for itself,
 * so it is stated here rather than assumed.
 */
import { WRIST_STATION_NAME, formatDigitPin } from '@harryhausen/kernel';
import type { AnimatorDocument, Confirmation, HandSession } from '@harryhausen/kernel';
import type { HandCandidate } from './hand-candidates.js';

/**
 * One answer and WHO gave it. Per-answer, because the two questions are not
 * asked together and are not always answered by the same person: on the android
 * fixture, Allen confirmed `hand-5` by eye while the thumb was never pinned by
 * anyone. A single `confirmedBy` across both forces a lie in one direction or
 * the other — understate Allen's real confirmation, or attribute a pin to him
 * he never made (Codex P2, #77).
 */
export interface HandAnswer {
  readonly value: string;
  /** `human:<id>` — never minted by engine code. */
  readonly confirmedBy: string;
}

export interface HandDeclaration {
  /** The §11 voxel knob for THIS hand — bands are per-hand, never global. */
  readonly resolution: number;
  /** The branch pinned as the thumb, e.g. `branch-1`. Omitted = leave as-is. */
  readonly thumbBranch?: HandAnswer;
  /** The digit profile declared, e.g. `hand-5`. Omitted = leave as-is. */
  readonly handProfile?: HandAnswer;
  /**
   * The digit PINS: named tips in BIND-WORLD, plus who placed them. Supplying
   * this REPLACES the hand's whole assignment (every prior `digitAssignment`
   * for this hand is cleared first) — a partial rewrite would leave a digit
   * from an earlier answer stranded on a place nobody reconsidered.
   *
   * A tip the list omits is NOT A DIGIT, which is the whole point: the
   * android's dorsal arch and the kobold's phantom finger are answered by
   * leaving them out, not by hunting a resolution where the volume agrees.
   *
   * WORLD, NOT BRANCH (ADR-022 §3.2). The surface has always HAD these
   * positions — a consensus cluster's `tip` is a world-space mean across every
   * resolution that saw it — and used to spend them resolving down to a
   * `branch-N` before writing. That threw away the durable thing and stored the
   * perishable one. The tip is what the person pointed at; it survives
   * re-measurement, re-opening, and the slider.
   */
  readonly digitAssignment?: {
    readonly pins: readonly {
      readonly digit: string;
      readonly tip: { readonly x: number; readonly y: number; readonly z: number };
      /**
       * The OPTIONAL second row (ADR-024 §2.2): this digit's first knuckle, in
       * BIND-WORLD. Written as its own `digitKnuckle` confirmation beside the
       * tip's `digitAssignment`. Rides the same wholesale-replace rule: a
       * re-declaration clears both rows for this hand together, because a
       * knuckle kept beside a re-pinned tip would aim the new digit with the
       * old row.
       */
      readonly knuckle?: { readonly x: number; readonly y: number; readonly z: number };
    }[];
    readonly confirmedBy: string;
  };
  /**
   * Recenter the wrist bind into the wrist volume (unbind-adjust-rebind #1) —
   * value is the METHOD, today only `'seed-centroid'`. A human act like the
   * others: the panel proposes with the measured offset; a trusted click signs.
   * null explicitly clears a saved answer; omission preserves it.
   */
  readonly wristRecenter?: HandAnswer | null;
  /**
   * WHERE THE WRIST IS (ADR-025) — a BIND-WORLD point the person clicked.
   * Written as a `wristStation` confirmation. Rides the digitAssignment's
   * wholesale-replace rule: a re-declaration of the hand's digits restates the
   * wrist too (present → replaced, absent → cleared), because a wrist kept
   * beside re-pinned tips would move a joint nobody looked at this time.
   */
  readonly wristStation?: {
    readonly point: { readonly x: number; readonly y: number; readonly z: number };
    readonly confirmedBy: string;
  };
  /** The thumb's opposition angle in degrees (field 2026-08-15) — written as `thumbOpposition`. */
  readonly thumbOpposition?: { readonly degrees: number; readonly confirmedBy: string };
  /**
   * REPLACE FINGERS (2026-09-29): the hand's existing digits go before the graft (`nativeDigits: 'replace'`).
   * Explicit, never inferred from the rig: after an in-session graft a fingerless hand also "has children".
   * Once set it stays with the hand across re-declarations until the hand is withdrawn.
   */
  readonly replaceNativeDigits?: boolean;
}

/**
 * Declare (or re-declare) one hand, returning the next Document for the loader
 * to gate. A resolution outside 8–128, a malformed branch id, an unknown
 * profile or a non-human `confirmedBy` all refuse at that gate — composed here,
 * never waved through.
 */
export function declareHand(
  doc: AnimatorDocument,
  candidate: HandCandidate,
  declaration: HandDeclaration,
): AnimatorDocument {
  const session: HandSession = {
    handBone: candidate.handBone,
    forearmBone: candidate.forearmBone,
    side: candidate.side,
    // 'declared' because a person moved the knob and watched the branches
    // settle. A resolution this code chose would be 'default', and the two must
    // never be confused — the provenance is the difference between a measured
    // band and a guess (§8).
    resolution: { value: declaration.resolution, provenance: 'declared' },
    ...(declaration.replaceNativeDigits === true || doc.target.hands.find((h) => h.handBone === candidate.handBone)?.nativeDigits === 'replace'
      ? { nativeDigits: 'replace' as const }
      : {}),
  };

  // Re-declaration REPLACES per affordance: the schema refuses a duplicate hand
  // session and a duplicate confirmation per affordance, so a second answer must
  // supersede the first rather than accumulate beside it. An answer NOT restated
  // is left exactly as it was, attribution included — moving the resolution or
  // re-pinning the thumb must never quietly re-sign the other answer in the
  // name of whoever happened to touch the knob last (Codex P2, #77).
  const hands = [...doc.target.hands.filter((h) => h.handBone !== candidate.handBone), session];
  const restated = new Set(
    [
      declaration.thumbBranch && 'thumbBranch',
      declaration.handProfile && 'handProfile',
      // Wholesale, not per-digit: see HandDeclaration.digitAssignment. The
      // knuckle row replaces WITH the tips it belongs to — always together.
      declaration.digitAssignment && 'digitAssignment',
      declaration.digitAssignment && 'digitKnuckle',
      (declaration.digitAssignment || declaration.wristStation) && 'wristStation',
      declaration.thumbOpposition && 'thumbOpposition',
      declaration.wristRecenter !== undefined && 'wristRecenter',
    ].filter(Boolean),
  );
  const confirmations: Confirmation[] = [
    ...doc.target.confirmations.filter((c) => !(c.scope === candidate.handBone && restated.has(c.target))),
    ...(declaration.thumbBranch
      ? [
          {
            target: 'thumbBranch' as const,
            scope: candidate.handBone,
            value: declaration.thumbBranch.value,
            confirmedBy: declaration.thumbBranch.confirmedBy,
          },
        ]
      : []),
    ...(declaration.handProfile
      ? [
          {
            target: 'handProfile' as const,
            scope: candidate.handBone,
            value: declaration.handProfile.value,
            confirmedBy: declaration.handProfile.confirmedBy,
          },
        ]
      : []),
    // One confirmation PER DIGIT — each is its own human act ("yes, those markers
    // are on the ring finger"), with its own attribution, lapsing on its own.
    ...(declaration.digitAssignment
      ? declaration.digitAssignment.pins.map((pin) => ({
          target: 'digitAssignment' as const,
          scope: candidate.handBone,
          value: formatDigitPin(pin.digit, pin.tip),
          confirmedBy: declaration.digitAssignment!.confirmedBy,
        }))
      : []),
    // The knuckle row (ADR-024 §2.2): one per digit that has one, same grammar,
    // beside its tip. The schema cross-checks that every knuckle has its tip.
    ...(declaration.digitAssignment
      ? declaration.digitAssignment.pins
          .filter((pin) => pin.knuckle !== undefined)
          .map((pin) => ({
            target: 'digitKnuckle' as const,
            scope: candidate.handBone,
            value: formatDigitPin(pin.digit, pin.knuckle!),
            confirmedBy: declaration.digitAssignment!.confirmedBy,
          }))
      : []),
    // The thumb's opposition (field 2026-08-15): one per hand, degrees.
    ...(declaration.thumbOpposition
      ? [
          {
            target: 'thumbOpposition' as const,
            scope: candidate.handBone,
            value: String(declaration.thumbOpposition.degrees),
            confirmedBy: declaration.thumbOpposition.confirmedBy,
          },
        ]
      : []),
    // The wrist station (ADR-025): one per hand, the person's click.
    ...(declaration.wristStation
      ? [
          {
            target: 'wristStation' as const,
            scope: candidate.handBone,
            value: formatDigitPin(WRIST_STATION_NAME, declaration.wristStation.point),
            confirmedBy: declaration.wristStation.confirmedBy,
          },
        ]
      : []),
    ...(declaration.wristRecenter
      ? [
          {
            target: 'wristRecenter' as const,
            scope: candidate.handBone,
            value: declaration.wristRecenter.value,
            confirmedBy: declaration.wristRecenter.confirmedBy,
          },
        ]
      : []),
  ];

  return { ...doc, target: { ...doc.target, hands, confirmations } };
}

/** Withdraw a hand and its confirmations — the un-declare, kept symmetric. */
export function withdrawHand(doc: AnimatorDocument, handBone: string): AnimatorDocument {
  return {
    ...doc,
    target: {
      ...doc.target,
      hands: doc.target.hands.filter((h) => h.handBone !== handBone),
      confirmations: doc.target.confirmations.filter((c) => c.scope !== handBone),
    },
  };
}

/** What {@link forgetReplacedDigitEdits} let go of — reported, never silent. */
export interface ForgottenDigitEdits {
  readonly doc: AnimatorDocument;
  /** Bones whose pose edit was dropped. */
  readonly poses: readonly string[];
  /** `clip:bone` for every key track dropped (the clip itself stays, with its other tracks). */
  readonly tracks: readonly string[];
}

/**
 * REPLACE FINGERS LETS GO OF WHAT NAMED THE OLD FINGERS (Codex P2, #226). The
 * run strips the hand's native digit chains and grafts `…_graft` bones in their
 * place, so a pose edit or an authored key track still naming a native digit
 * refuses at the run's missing-bone gates — the replace worked and the next
 * run died of it.
 *
 * CLEARED, NOT REMAPPED. The graft's names are knowable (ontology id + `_graft`)
 * but its bones are not the old bones: a pose or a key is a LOCAL rotation in
 * its bone's bind frame, the graft seats each digit in a frame of its own from
 * the pins, and a quaternion carried across frames is a different pose wearing
 * the old one's name (the played-frame lesson). It is also not always there to
 * carry to — a partial replace grafts fewer digits than it strips. So those
 * entries go, the same idiom the new-subject lineage uses for a rig change
 * (`beginTargetLineage`: "authored keys and pose edits name bones this skeleton
 * may not have"), scoped to the ONE hand: every other pose and track stays.
 *
 * `digitBones` is what the strip will remove — the caller reads it off the live
 * rig with `stripDigitsFromRig(rig, handBone).removed`, the run's own rule, so
 * the door and the run cannot disagree about which bones go. The return names
 * what was dropped: a loss the person did not see is the silent kind (a refusal
 * must reach the eye), so the caller says it on the status line.
 */
export function forgetReplacedDigitEdits(doc: AnimatorDocument, digitBones: readonly string[]): ForgottenDigitEdits {
  const gone = new Set(digitBones);
  const poses = doc.edits.poses.filter((p) => gone.has(p.bone)).map((p) => p.bone);
  const tracks = doc.clips.flatMap((c) => c.tracks.filter((t) => gone.has(t.bone)).map((t) => `${c.name}:${t.bone}`));
  if (poses.length === 0 && tracks.length === 0) return { doc, poses, tracks };
  return {
    doc: {
      ...doc,
      clips: doc.clips.map((c) => (c.tracks.some((t) => gone.has(t.bone)) ? { ...c, tracks: c.tracks.filter((t) => !gone.has(t.bone)) } : c)),
      edits: { ...doc.edits, poses: doc.edits.poses.filter((p) => !gone.has(p.bone)) },
    },
    poses,
    tracks,
  };
}
